import { addMinutes } from "date-fns";
import { useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { useHistory } from "react-router";

import WebinarRegistrationButton from "@components/v1/buttons/WebinarRegistrationButton";
import styles from "@components/v1/WebinarActions.module.css";
import Button from "@components/v2/buttons/Button";
import Text from "@components/v2/typography/Text";
import useMe from "@hooks/useMe";
import useMyClient from "@hooks/useMyClient";
import { automaticallyRegisterForArticleIdKey, automaticallyRegisterForArticleStartTimeKey } from "@typing/Constants";
import { ArticleContentTypeEnum, ArticleFragment } from "@typing/Generated";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  article: ArticleFragment;
  isWebinar: boolean;
  journeyId: string | null;
};

const WebinarActions = ({ article, isWebinar, journeyId }: Props) => {
  const history = useHistory();
  const myClient = useMyClient();
  const myUser = useMe();
  const isPastWebinar = useMemo(() => {
    if (article.contentType === ArticleContentTypeEnum.WEBINAR && article.webinarDuration) {
      if (article.webinarCompleted || addMinutes(article.webinarStartTime, article.webinarDuration) < new Date()) {
        return true;
      }
    }

    return false;
  }, [article.contentType, article.webinarCompleted, article.webinarDuration, article.webinarStartTime]);

  const handleActivateToRegister = () => {
    sessionStorage.setItem(automaticallyRegisterForArticleIdKey, article.id);
    sessionStorage.setItem(automaticallyRegisterForArticleStartTimeKey, new Date().getTime().toString());
    history.push("/sign-up");
  };

  const exploreLink =
    myClient &&
    journeyId &&
    journeyRoute({
      journeyId,
      linkAfterJourney: "/explore?exploreTab=EVENTS",
      myClient
    });

  return (
    <>
      {/*
          If it is an incomplete webinar then we want to let you register for it if you are a client. If you
          are not logged in, we want to show you a thing that encourages you to register in order to attend.
          If you are logged in as a non-client, you get nuttin. If the event has already occurred then we need
          to let the user know and switch up the link with one that takes them to the explore page.
      */}
      {isWebinar && (
        <>
          {isPastWebinar ? (
            <>
              {exploreLink ? (
                <>
                  <Text italic size="lg">
                    <FormattedMessage id="pages.article.thisEventHasEnded" />
                  </Text>
                  <Button inline routerLink={exploreLink}>
                    <FormattedMessage id="pages.article.exploreAllEvents" />
                  </Button>
                </>
              ) : (
                <>
                  <div>
                    <Text italic size="lg">
                      <FormattedMessage id="pages.article.thisEventHasEndedNoUser" />
                    </Text>
                  </div>
                  <Button inline routerLink="/activate">
                    <FormattedMessage id="pages.article.activateToExplore" />
                  </Button>
                </>
              )}
            </>
          ) : (
            <>
              {myClient ? (
                <WebinarRegistrationButton article={article} journeyId={journeyId} />
              ) : (
                <>
                  {!myUser && (
                    <>
                      <div className={styles.activateToRegisterTxt}>
                        <Text size="lg">
                          <FormattedMessage id="pages.explore.events.activateToRegisterExplainer" />
                        </Text>
                      </div>
                      <Button inline onClick={handleActivateToRegister}>
                        <FormattedMessage id="pages.explore.events.activateToRegister" />
                      </Button>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default WebinarActions;
